import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Settings, 
  Palette, 
  FileText, 
  Image, 
  Layout,
  Eye,
  LogOut,
  BarChart3,
  Zap,
  Layers,
  MousePointer,
  Smartphone,
  Plus
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { CMSGeneralSettings } from '@/components/cms/CMSGeneralSettings'
import { CMSColorSettings } from '@/components/cms/CMSColorSettings'
import { CMSContentEditor } from '@/components/cms/CMSContentEditor'
import { CMSMediaLibrary } from '@/components/cms/CMSMediaLibrary'
import { CMSPageEditor } from '@/components/cms/CMSPageEditor'
import { CMSVisualEditor } from '@/components/cms/CMSVisualEditor'
import { CMSThemeManager } from '@/components/cms/CMSThemeManager'
import { CMSAnalytics } from '@/components/cms/CMSAnalytics'

const AdminCMSPage = () => {
  const { user, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState('general')
  const [selectedPageForEditor, setSelectedPageForEditor] = useState<string | null>(null)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CMS Professionnel</h1>
            <p className="text-gray-600">Gestionnaire de contenu avancé pour webdesigners</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm">
                <Layout className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Voir le site
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {selectedPageForEditor ? (
          <div className="mb-4">
            <Button 
              variant="outline" 
              onClick={() => setSelectedPageForEditor(null)}
              className="mb-4"
            >
              ← Retour au CMS
            </Button>
            <CMSVisualEditor pageId={selectedPageForEditor} />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-8 mb-8">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Général
              </TabsTrigger>
              <TabsTrigger value="visual" className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Éditeur
              </TabsTrigger>
              <TabsTrigger value="design" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Design
              </TabsTrigger>
              <TabsTrigger value="pages" className="flex items-center gap-2">
                <Layout className="w-4 h-4" />
                Pages
              </TabsTrigger>
              <TabsTrigger value="content" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Contenu
              </TabsTrigger>
              <TabsTrigger value="media" className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Médias
              </TabsTrigger>
              <TabsTrigger value="popups" className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Pop-ups
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Analytics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>Paramètres Généraux</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSGeneralSettings />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="visual">
              <Card>
                <CardHeader>
                  <CardTitle>Éditeur Visuel en Temps Réel</CardTitle>
                  <p className="text-gray-600">
                    Sélectionnez une page à éditer avec l'éditeur visuel avancé
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {['home', 'expertise', 'blog'].map((pageSlug) => (
                      <Card key={pageSlug} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardContent className="p-4" onClick={() => setSelectedPageForEditor(pageSlug)}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold capitalize">{pageSlug}</h3>
                              <p className="text-sm text-gray-500">Éditer visuellement</p>
                            </div>
                            <div className="flex gap-2">
                              <Smartphone className="w-4 h-4 text-gray-400" />
                              <Eye className="w-4 h-4 text-blue-500" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="design">
              <Card>
                <CardHeader>
                  <CardTitle>Design System & Thèmes</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSThemeManager />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pages">
              <Card>
                <CardHeader>
                  <CardTitle>Gestionnaire de Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSPageEditor />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="content">
              <Card>
                <CardHeader>
                  <CardTitle>Éditeur de Contenu</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSContentEditor />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="media">
              <Card>
                <CardHeader>
                  <CardTitle>Bibliothèque Média</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSMediaLibrary />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="popups">
              <Card>
                <CardHeader>
                  <CardTitle>Gestionnaire de Pop-ups</CardTitle>
                  <p className="text-gray-600">
                    Créez et gérez des pop-ups pour augmenter l'engagement
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Zap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Gestionnaire de pop-ups en cours de développement</p>
                    <Button variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une pop-up
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analytics">
              <Card>
                <CardHeader>
                  <CardTitle>Analytics & Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <CMSAnalytics />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  )
}

export default AdminCMSPage
